import React, { useState, useEffect, useRef } from 'react';
import './UploadView.css';
import '../../styles/DataGrid.css';
import Card from '../common/Card';
import Table from '../common/Table';
import Pagination from '../common/Pagination';
import Modal from '../common/Modal';
import { uploadApi, UploadedFile } from '../../services/uploadApi';
import { auth } from '../../services/api';

interface UploadViewProps {
  initialFilter?: 'pending' | 'failed';
}

const UploadView: React.FC<UploadViewProps> = ({ initialFilter }) => {
  const [uploadedFiles, setUploadedFiles] = useState<UploadedFile[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [uploadStats, setUploadStats] = useState({
    today: 0,
    week: 0,
    month: 0
  });
  const [currentPage, setCurrentPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [isDragging, setIsDragging] = useState(false);
  const [schedulerTime, setSchedulerTime] = useState<string>('02:00');
  const [pdfModalOpen, setPdfModalOpen] = useState(false);
  const [pdfUrl, setPdfUrl] = useState<string>('');
  const [pdfFileName, setPdfFileName] = useState<string>('');
  const [activeFilter, setActiveFilter] = useState<'pending' | 'failed'>(initialFilter || 'pending');
  const [pendingCount, setPendingCount] = useState(0);
  const [failedCount, setFailedCount] = useState(0);
  const [uploadConfig, setUploadConfig] = useState<{maxFiles: number, maxFileSize: number, allowedTypes: string[]} | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Calculate next processing time based on scheduler config
  const getNextProcessingTime = () => {
    const now = new Date();
    const [hours, minutes] = schedulerTime.split(':').map(Number);
    const nextRun = new Date();
    nextRun.setHours(hours, minutes, 0, 0);
    
    // If it's already past the scheduled time today, set to tomorrow
    if (now.getHours() > hours || (now.getHours() === hours && now.getMinutes() >= minutes)) {
      nextRun.setDate(nextRun.getDate() + 1);
    }
    
    // Check if it's today or tomorrow
    const isToday = nextRun.toDateString() === now.toDateString();
    const tomorrow = new Date(now);
    tomorrow.setDate(tomorrow.getDate() + 1);
    const isTomorrow = nextRun.toDateString() === tomorrow.toDateString();
    
    const timeStr = nextRun.toLocaleTimeString('en-US', {
      hour: 'numeric',
      minute: '2-digit',
      hour12: true
    });
    
    if (isToday) {
      return `Today at ${timeStr}`;
    } else if (isTomorrow) {
      return `Tomorrow at ${timeStr}`;
    } else {
      const dateStr = nextRun.toLocaleDateString('en-US', {
        month: 'short',
        day: 'numeric'
      });
      return `${dateStr} at ${timeStr}`;
    }
  };

  useEffect(() => {
    loadData();
    loadSchedulerConfig();
    loadCounts();
  }, [activeFilter]);

  useEffect(() => {
    loadCounts();
    loadUploadConfig();
  }, []);

  const loadCounts = async () => {
    try {
      const [pendingFiles, failedFiles] = await Promise.all([
        uploadApi.getPendingFiles(),
        uploadApi.getFailedFiles()
      ]);
      setPendingCount(pendingFiles.length);
      setFailedCount(failedFiles.length);
    } catch (err) {
      console.error('Failed to load counts:', err);
    }
  };

  const loadUploadConfig = async () => {
    try {
      const config = await uploadApi.getConfig();
      setUploadConfig(config);
    } catch (err) {
      console.error('Failed to load upload config:', err);
    }
  };

  const loadSchedulerConfig = async () => {
    try {
      const response = await fetch('/api/system/scheduler', {
        headers: { ...auth.getAuthHeaders() }
      });
      const data = await response.json();
      if (data.time) {
        setSchedulerTime(data.time);
      }
    } catch (err) {
      console.error('Failed to load scheduler config:', err);
      // Keep default of 02:00
    }
  };

  const loadData = async () => {
    try {
      setLoading(true);
      const [filesData, statsData] = await Promise.all([
        activeFilter === 'pending' 
          ? uploadApi.getPendingFiles()
          : uploadApi.getFailedFiles(),
        uploadApi.getUploadStats()
      ]);
      setUploadedFiles(filesData);
      setUploadStats(statsData);
      setError(null);
    } catch (err) {
      console.error('Failed to load upload data:', err);
      setError('Failed to load upload data. Please check your connection and try again.');
      setUploadedFiles([]);
      setUploadStats({ today: 0, week: 0, month: 0 });
    } finally {
      setLoading(false);
    }
  };


  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(true);
  };

  const handleDragLeave = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(false);
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(false);

    const files = Array.from(e.dataTransfer.files);
    const validFiles = files.filter(file => file.type === 'application/pdf');

    if (files.length > validFiles.length) {
      alert('Only PDF files are accepted');
    }

    if (validFiles.length > 0) {
      setSelectedFiles(prev => {
        const newTotal = prev.length + validFiles.length;
        const maxFiles = uploadConfig?.maxFiles;

        if (maxFiles && newTotal > maxFiles) {
          alert(`Cannot add ${validFiles.length} file(s). Maximum ${maxFiles} files allowed. Currently have ${prev.length} file(s) selected.`);
          return prev;
        }

        return [...prev, ...validFiles];
      });
    }
  };

  const [selectedFiles, setSelectedFiles] = useState<File[]>([]);
  const [uploading, setUploading] = useState(false);

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files) {
      const files = Array.from(e.target.files);
      const validFiles = files.filter(file => file.type === 'application/pdf');

      if (files.length > validFiles.length) {
        alert('Only PDF files are accepted');
      }

      if (validFiles.length > 0) {
        setSelectedFiles(prev => {
          const newTotal = prev.length + validFiles.length;
          const maxFiles = uploadConfig?.maxFiles;

          if (maxFiles && newTotal > maxFiles) {
            alert(`Cannot add ${validFiles.length} file(s). Maximum ${maxFiles} files allowed. Currently have ${prev.length} file(s) selected.`);
            return prev;
          }

          return [...prev, ...validFiles];
        });
      }
    }
  };

  const handleUploadClick = async () => {
    if (selectedFiles.length === 0) {
      alert('Please select files to upload');
      return;
    }

    setUploading(true);
    try {
      // Upload all selected files to S3
      const result = await uploadApi.uploadFiles(selectedFiles);
      console.log('Upload result:', result);
      
      // Clear selected files and refresh the list
      setSelectedFiles([]);
      await loadData();
      await loadCounts();
      alert(`Successfully uploaded ${selectedFiles.length} file(s)`);
    } catch (error) {
      console.error('Upload failed:', error);
      alert(`Failed to upload files: ${error instanceof Error ? error.message : 'Unknown error'}`);
    } finally {
      setUploading(false);
    }
  };

  const removeSelectedFile = (index: number) => {
    setSelectedFiles(prev => prev.filter((_, i) => i !== index));
  };

  const handleView = async (file: UploadedFile) => {
    try {
      const { url } = await uploadApi.getFileUrl(file.id);
      setPdfUrl(url);
      setPdfFileName(file.originalFilename);
      setPdfModalOpen(true);
    } catch (err) {
      console.error('Failed to get file URL:', err);
      alert('Failed to open file. Please try again.');
    }
  };

  const handleClosePdfModal = () => {
    setPdfModalOpen(false);
    setPdfUrl('');
    setPdfFileName('');
  };

  const handleDelete = async (file: UploadedFile) => {
    if (window.confirm(`Are you sure you want to delete ${file.originalFilename}?`)) {
      try {
        await uploadApi.deleteFile(file.id);
        await loadData();
        await loadCounts();
        alert('File deleted successfully');
      } catch (err) {
        console.error('Failed to delete file:', err);
        alert('Failed to delete file. Please try again.');
      }
    }
  };

  const handleRetry = async (file: UploadedFile) => {
    try {
      const result = await uploadApi.retryFailedFile(file.id);
      if (result.success) {
        alert(result.message);
        await loadData();
        await loadCounts();
      }
    } catch (error) {
      console.error('Error retrying file:', error);
      alert('Failed to retry file processing');
    }
  };

  const formatFileSize = (bytes: number): string => {
    if (bytes < 1024) return bytes + ' B';
    if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' KB';
    return (bytes / (1024 * 1024)).toFixed(1) + ' MB';
  };

  // Pagination logic
  const totalPages = Math.ceil(uploadedFiles.length / rowsPerPage);
  const startIndex = (currentPage - 1) * rowsPerPage;
  const endIndex = startIndex + rowsPerPage;
  const paginatedFiles = uploadedFiles.slice(startIndex, endIndex);

  const columns = [
    {
      key: 'originalFilename',
      title: 'Original File Name',
      width: '30%'
    },
    {
      key: 'systemFilename',
      title: 'System File Name',
      width: '30%'
    },
    {
      key: 'size',
      title: 'Size',
      width: '7%'
    },
    {
      key: 'uploadedBy',
      title: 'Uploaded By',
      width: '11%'
    },
    {
      key: 'uploadedAt',
      title: 'Uploaded At',
      width: '11%'
    },
    {
      key: 'actions',
      title: 'Actions',
      width: '11%'
    }
  ];

  const transformedData = paginatedFiles.map(file => ({
    originalFilename: (
      <div className="filename-cell">
        <span className="filename">{file.originalFilename}</span>
      </div>
    ),
    systemFilename: (
      <span className="system-id" title={file.systemFilename}>
        {file.systemFilename.split('/').pop()}
      </span>
    ),
    size: <span className="file-size">{formatFileSize(file.fileSize)}</span>,
    uploadedBy: <span className="uploader">{file.uploadedBy}</span>,
    uploadedAt: (
      <span className="upload-date">
        {(() => {
          const date = new Date(file.uploadedAt);
          const dateStr = date.toLocaleDateString('en-US', {
            month: '2-digit',
            day: '2-digit',
            year: 'numeric'
          });
          const timeStr = date.toLocaleTimeString('en-US', {
            hour: '2-digit',
            minute: '2-digit',
            hour12: true
          });
          return `${dateStr} ${timeStr}`;
        })()}
      </span>
    ),
    actions: (
      <div className="file-actions">
        <button 
          className="action-icon-btn view"
          onClick={() => handleView(file)}
          title="View file"
        >
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/>
            <circle cx="12" cy="12" r="3"/>
          </svg>
        </button>
        {activeFilter === 'failed' && (
          <button 
            className="action-icon-btn retry"
            onClick={() => handleRetry(file)}
            title="Retry processing"
          >
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <polyline points="23 4 23 10 17 10"/>
              <path d="M20.49 15a9 9 0 1 1-2.12-9.36L23 10"/>
            </svg>
          </button>
        )}
        <button 
          className="action-icon-btn delete"
          onClick={() => handleDelete(file)}
          title="Delete file"
        >
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <polyline points="3 6 5 6 21 6"/>
            <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"/>
            <line x1="10" y1="11" x2="10" y2="17"/>
            <line x1="14" y1="11" x2="14" y2="17"/>
          </svg>
        </button>
      </div>
    )
  }));


  return (
    <div className="upload-view">
      <Card className="upload-section">
        <div className="upload-header">
          <h3>Upload New Invoices</h3>
          <div className="processing-time">
            <span className="time-text">Next Processing: {getNextProcessingTime()}</span>
          </div>
        </div>
        
        <div className="upload-content">
          <div 
            className={`upload-zone ${isDragging ? 'dragging' : ''}`}
            onDragOver={handleDragOver}
            onDragLeave={handleDragLeave}
            onDrop={handleDrop}
            onClick={() => fileInputRef.current?.click()}
          >
            <div className="upload-icon">📤</div>
            <div className="upload-text">
              <p className="upload-main">Drop PDF files here</p>
              <p className="upload-sub">or click to browse</p>
              {uploadConfig && (
                <p className="upload-limits">
                  Max {uploadConfig.maxFiles} files, {(uploadConfig.maxFileSize / (1024 * 1024)).toFixed(0)}MB per file
                </p>
              )}
            </div>
            <input
              ref={fileInputRef}
              type="file"
              multiple
              accept=".pdf"
              onChange={handleFileSelect}
              style={{ display: 'none' }}
            />
          </div>

          <div className="selected-files">
            <h4>Selected Files ({selectedFiles.length})</h4>
            {selectedFiles.length > 0 ? (
              <>
                <div className="files-grid">
                  {selectedFiles.map((file, index) => (
                    <div key={index} className="file-item">
                      <span className="file-name" title={file.name}>
                        {file.name.length > 25 ? file.name.substring(0, 22) + '...' : file.name}
                      </span>
                      <span className="file-size">{formatFileSize(file.size)}</span>
                      <button 
                        className="remove-file"
                        onClick={() => removeSelectedFile(index)}
                        title="Remove"
                      >
                        ✕
                      </button>
                    </div>
                  ))}
                </div>
                <div className="upload-actions">
                  <button 
                    className="clear-btn"
                    onClick={() => setSelectedFiles([])}
                    disabled={uploading}
                  >
                    Clear All
                  </button>
                  <button 
                    className="upload-btn"
                    onClick={handleUploadClick}
                    disabled={uploading}
                  >
                    {uploading ? 'Uploading...' : 'Upload Files'}
                  </button>
                </div>
              </>
            ) : (
              <div className="no-selection">
                <p>No files selected</p>
              </div>
            )}
          </div>
        </div>
      </Card>

      <Card className="data-grid-table">
        <div className="data-grid-navigation">
          <div className="data-grid-tabs">
            <button 
              className={`data-grid-tab ${activeFilter === 'pending' ? 'active' : ''}`}
              onClick={() => setActiveFilter('pending')}
            >
              Pending Files <span className="count">{pendingCount}</span>
            </button>
            <button 
              className={`data-grid-tab ${activeFilter === 'failed' ? 'active' : ''}`}
              onClick={() => setActiveFilter('failed')}
            >
              Failed <span className="count">{failedCount}</span>
            </button>
          </div>
          <div className="data-grid-rows-control">
            <span>Show</span>
            <select 
              value={rowsPerPage}
              onChange={(e) => setRowsPerPage(Number(e.target.value))}
            >
              <option value={10}>10 rows</option>
              <option value={25}>25 rows</option>
              <option value={50}>50 rows</option>
              <option value={100}>100 rows</option>
            </select>
          </div>
        </div>

        <div className="data-grid-body">
          {uploadedFiles.length > 0 ? (
            <>
              <Table
                columns={columns}
                data={transformedData}
              />
              <Pagination
                currentPage={currentPage}
                totalPages={totalPages}
                rowsPerPage={rowsPerPage}
                totalItems={uploadedFiles.length}
                onPageChange={setCurrentPage}
                onRowsPerPageChange={setRowsPerPage}
              />
            </>
          ) : (
            <div className="data-grid-empty">
              <p>No pending files.</p>
            </div>
          )}
        </div>
      </Card>

      {pdfModalOpen && (
        <Modal
          isOpen={pdfModalOpen}
          onClose={handleClosePdfModal}
          title={pdfFileName}
          size="medium"
        >
          <iframe
            src={pdfUrl}
            style={{ width: '100%', height: '70vh', border: 'none' }}
            title={pdfFileName}
          />
        </Modal>
      )}
    </div>
  );
};

export default UploadView;